import { computed, getCurrentScope, onScopeDispose, readonly, Ref, ref, shallowRef } from 'vue';
import { RecordedAnswer } from '@/types/TaskConfig';

/** eprocom-backend#529 §5: the same limit for every task, not in the config. */
export const MAX_RECORDING_MS = 120000;
/** The clock signals the last seconds before the automatic stop. */
export const ENDING_SOON_MS = 10000;
/** eprocom-backend#529 §8.3: ~1 MB for 2 minutes of speech. The browser may round it. */
export const AUDIO_BITS_PER_SECOND = 64000;
const CLOCK_TICK_MS = 250;
/** A recorder already inactive should still deliver its `stop` event; past this, it is finalized anyway. */
const STOP_EVENT_FALLBACK_MS = 1000;

export type RecorderState = 'idle' | 'starting' | 'recording' | 'recorded' | 'unavailable';
export type MicUnavailableCause = 'denied' | 'no_device' | 'busy' | 'unsupported';
export type RecordEndCause = 'manual' | 'automatic' | 'interruption';

export interface AudioRecorderEvents {
    onStarted(): void;
    onEnded(durationMs: number, cause: RecordEndCause): void;
    onUnavailable(cause: MicUnavailableCause): void;
    onReplayStarted(): void;
}

export interface AudioRecorderOptions {
    /** Asked once the microphone is open: false releases it and goes back to rest, silently. */
    canStart?: () => boolean;
}

export interface AudioRecorder {
    state: Readonly<Ref<RecorderState>>;
    elapsedMs: Readonly<Ref<number>>;
    isEndingSoon: Readonly<Ref<boolean>>;
    recording: Readonly<Ref<RecordedAnswer | null>>;
    unavailableCause: Readonly<Ref<MicUnavailableCause | null>>;
    isReplaying: Readonly<Ref<boolean>>;
    start(): Promise<void>;
    stop(cause: RecordEndCause): Promise<void>;
    retry(): Promise<void>;
    replay(): Promise<void>;
    stopReplay(): void;
    discard(): void;
    dispose(): void;
}

/** Recorders still alive, so that the end of a session can stop them all (`App.vue`). */
const liveRecorders = new Set<AudioRecorder>();

export function stopAllAudioRecorders(): void {
    liveRecorders.forEach((recorder) => {
        recorder.stopReplay();
        recorder.stop('interruption');
    });
}

/** Turns what getUserMedia throws into the cause the telemetry reports (eprocom-backend#529 §6, §9). */
export function mapMicError(error: unknown): MicUnavailableCause {
    switch ((error as { name?: unknown } | null)?.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
        case 'SecurityError':
            return 'denied';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
            return 'no_device';
        default:
            return 'busy';
    }
}

function isRecordingSupported(): boolean {
    return (
        typeof navigator !== 'undefined' &&
        !!navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        typeof MediaRecorder === 'function'
    );
}

function createMediaRecorder(stream: MediaStream): MediaRecorder {
    try {
        return new MediaRecorder(stream, { audioBitsPerSecond: AUDIO_BITS_PER_SECOND });
    } catch {
        // A browser that refuses the bitrate still records at its default one.
        return new MediaRecorder(stream);
    }
}

/**
 * eprocom-pwa#857 — one microphone recorder, local to the component that creates it (the V1
 * `useRecording` module shares one global state). The microphone is open only while recording.
 */
export default function useAudioRecorder(
    events: AudioRecorderEvents,
    options: AudioRecorderOptions = {}
): AudioRecorder {
    const state = ref<RecorderState>('idle');
    const elapsedMs = ref(0);
    const recording = shallowRef<RecordedAnswer | null>(null);
    const unavailableCause = ref<MicUnavailableCause | null>(null);
    const isReplaying = ref(false);
    const isEndingSoon = computed(
        () => state.value === 'recording' && elapsedMs.value >= MAX_RECORDING_MS - ENDING_SOON_MS
    );

    let stream: MediaStream | null = null;
    let mediaRecorder: MediaRecorder | null = null;
    let chunks: Blob[] = [];
    let startedAt = 0;
    let stoppedAt: number | null = null;
    let endCause: RecordEndCause = 'manual';
    let clockTimer: ReturnType<typeof setInterval> | null = null;
    let limitTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingStop: Promise<void> | null = null;
    let resolvePendingStop: (() => void) | null = null;
    let audio: HTMLAudioElement | null = null;
    let objectUrl: string | null = null;
    let disposed = false;

    const releaseStream = (): void => {
        stream?.getTracks().forEach((track) => track.stop());
        stream = null;
    };

    const clearTimers = (): void => {
        if (clockTimer !== null) clearInterval(clockTimer);
        if (limitTimer !== null) clearTimeout(limitTimer);
        clockTimer = null;
        limitTimer = null;
    };

    const elapsedSinceStart = (): number => Math.min(Date.now() - startedAt, MAX_RECORDING_MS);

    const stopReplay = (): void => {
        if (audio && isReplaying.value) {
            audio.pause();
        }
        isReplaying.value = false;
    };

    const forgetRecording = (): void => {
        stopReplay();
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
        objectUrl = null;
        audio = null;
        recording.value = null;
    };

    const becomeUnavailable = (cause: MicUnavailableCause): void => {
        releaseStream();
        mediaRecorder = null;
        state.value = 'unavailable';
        unavailableCause.value = cause;
        events.onUnavailable(cause);
    };

    const openMicrophone = async (): Promise<MediaStream | null> => {
        if (!isRecordingSupported()) {
            becomeUnavailable('unsupported');
            return null;
        }
        try {
            return await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        } catch (error) {
            becomeUnavailable(mapMicError(error));
            return null;
        }
    };

    const finalize = (): void => {
        clearTimers();
        const durationMs = stoppedAt ?? elapsedSinceStart();
        const mimeType = mediaRecorder?.mimeType || chunks[0]?.type || '';
        const file = chunks.length === 1 ? chunks[0] : new Blob(chunks, { type: mimeType });
        releaseStream();
        mediaRecorder = null;
        chunks = [];
        stoppedAt = null;

        if (!disposed) {
            if (file.size > 0) {
                recording.value = { state: 'recorded', file, mimeType, durationMs };
                state.value = 'recorded';
            } else {
                state.value = 'idle';
            }
        }

        events.onEnded(durationMs, endCause);
        resolvePendingStop?.();
        resolvePendingStop = null;
        pendingStop = null;
    };

    const stop = (cause: RecordEndCause): Promise<void> => {
        if (pendingStop) return pendingStop;
        if (state.value !== 'recording' || !mediaRecorder) return Promise.resolve();

        endCause = cause;
        clearTimers();
        stoppedAt = elapsedSinceStart();
        elapsedMs.value = stoppedAt;
        pendingStop = new Promise<void>((resolve) => {
            resolvePendingStop = resolve;
        });

        const current = mediaRecorder;
        if (current.state !== 'inactive') {
            current.stop();
        } else {
            /* The browser already stopped it (a fatal error, the microphone taken away): its `stop` event
             * is on its way with the data. Finalize from there, once — or here if it never comes. */
            setTimeout(() => {
                if (mediaRecorder === current) finalize();
            }, STOP_EVENT_FALLBACK_MS);
        }

        return pendingStop;
    };

    const start = async (): Promise<void> => {
        if (disposed || state.value === 'starting' || state.value === 'recording') return;

        // Re-recording replaces: the previous recording goes at once (eprocom-backend#529 §5).
        forgetRecording();
        unavailableCause.value = null;
        state.value = 'starting';

        const opened = await openMicrophone();
        if (!opened) return;
        stream = opened;

        if (disposed || (options.canStart && !options.canStart())) {
            releaseStream();
            if (!disposed) state.value = 'idle';
            return;
        }

        let recorder: MediaRecorder;
        try {
            recorder = createMediaRecorder(opened);
        } catch {
            becomeUnavailable('unsupported');
            return;
        }
        mediaRecorder = recorder;
        chunks = [];
        endCause = 'manual';

        recorder.addEventListener('dataavailable', (event: BlobEvent) => {
            if (event.data && event.data.size > 0) {
                chunks.push(event.data);
            }
        });
        recorder.addEventListener('stop', () => {
            // Each recording is finalized once, whatever stopped it.
            if (mediaRecorder !== recorder) return;
            if (!pendingStop) {
                // Nobody asked: the browser stopped recording on its own.
                endCause = 'interruption';
                stoppedAt = elapsedSinceStart();
            }
            finalize();
        });
        // The browser cutting the microphone (call, lock, another app) is an interruption: kept like a stop.
        opened.getAudioTracks().forEach((track) => track.addEventListener('ended', () => stop('interruption')));

        await new Promise<void>((resolve) => {
            recorder.addEventListener('error', () => {
                if (state.value === 'recording') {
                    stop('interruption');
                } else {
                    becomeUnavailable('busy');
                    resolve();
                }
            });
            recorder.addEventListener('start', () => {
                startedAt = Date.now();
                elapsedMs.value = 0;
                state.value = 'recording';
                clockTimer = setInterval(() => {
                    elapsedMs.value = elapsedSinceStart();
                }, CLOCK_TICK_MS);
                limitTimer = setTimeout(() => stop('automatic'), MAX_RECORDING_MS);
                events.onStarted();
                resolve();
            });
            try {
                recorder.start();
            } catch {
                becomeUnavailable('unsupported');
                resolve();
            }
        });
    };

    /** « Réessayer » only tests the access: the student then taps the microphone to record. */
    const retry = async (): Promise<void> => {
        if (disposed || state.value === 'starting' || state.value === 'recording') return;

        state.value = 'starting';
        const opened = await openMicrophone();
        if (!opened) return;

        opened.getTracks().forEach((track) => track.stop());
        unavailableCause.value = null;
        state.value = 'idle';
    };

    const replay = async (): Promise<void> => {
        const kept = recording.value;
        if (!kept || state.value !== 'recorded' || isReplaying.value) return;

        if (!objectUrl) {
            objectUrl = URL.createObjectURL(kept.file);
        }
        if (!audio) {
            audio = document.createElement('audio');
            audio.src = objectUrl;
            audio.addEventListener('ended', () => {
                isReplaying.value = false;
            });
        }

        try {
            audio.currentTime = 0;
            await audio.play();
        } catch {
            isReplaying.value = false;
            return;
        }
        isReplaying.value = true;
        events.onReplayStarted();
    };

    const discard = (): void => {
        if (state.value !== 'recorded') return;
        forgetRecording();
        state.value = 'idle';
    };

    let recorderApi: AudioRecorder;

    const dispose = (): void => {
        if (disposed) return;
        liveRecorders.delete(recorderApi);
        stop('interruption');
        disposed = true;
        stopReplay();
        clearTimers();
        releaseStream();
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }
    };

    recorderApi = {
        state: readonly(state),
        elapsedMs: readonly(elapsedMs),
        isEndingSoon,
        recording: readonly(recording) as Readonly<Ref<RecordedAnswer | null>>,
        unavailableCause: readonly(unavailableCause),
        isReplaying: readonly(isReplaying),
        start,
        stop,
        retry,
        replay,
        stopReplay,
        discard,
        dispose,
    };

    liveRecorders.add(recorderApi);
    if (getCurrentScope()) {
        onScopeDispose(dispose);
    }

    return recorderApi;
}
